import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { getToken, setToken, removeToken, setStoreName, removeStoreName } from '../utils/storage';
import { authApi } from '../api';
import { t } from './LocaleContext';
import { setStoreTimezone, getBrowserTimezone } from '../utils/timezone';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  fetchMe: () => Promise<any>;
  isStoreCustomer: boolean;
  isFinance: boolean;
  isAdmin: boolean;
  hasRole: (roles: string[]) => boolean;
  auditStatus: number | null;
  canOrder: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  fetchMe: async () => null,
  isStoreCustomer: false,
  isFinance: false,
  isAdmin: false,
  hasRole: () => false,
  auditStatus: null,
  canOrder: true,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res: any = await authApi.me();
      if (res?.code === 0) {
        setUser(res.data);
        if (res.data?.timezone) {
          setStoreTimezone(res.data.timezone);
        }
        if (res.data?.roles?.includes('store_customer') && res.data?.store_name) {
          setStoreName(res.data.store_name);
        } else {
          removeStoreName();
        }
        return res.data;
      }
    } catch (err: any) {
      // 仅对认证类错误清除 token；网络/500 等临时错误保留 token，避免误登出
      const status = err?.status ?? err?.response?.status;
      const code = err?.code;
      const isAuthError =
        status === 401 ||
        status === 403 ||
        code === 401001 ||
        code === 401002 ||
        code === 400005;
      if (isAuthError) {
        removeToken();
      } else {
        // eslint-disable-next-line no-console
        console.warn('[AuthContext] /auth/me failed, keep token:', err?.message || err);
      }
    } finally {
      setLoading(false);
    }
    return null;
  }, []);

  useEffect(() => {
    if (getToken()) {
      fetchMe();
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  const login = async (username: string, password: string) => {
    const res: any = await authApi.login(username, password, getBrowserTimezone());
    if (res?.code === 0) {
      const roles = res.data?.user?.roles || [];
      // user_web_code 仅允许门店客户角色登录
      if (!roles.includes('store_customer')) {
        throw new Error(t('该账号无权限登录门店端'));
      }
      flushSync(() => {
        const expiresIn = res.data?.expires_in || 2592000;
        setToken(res.data.token, expiresIn * 1000);
        setUser(res.data.user);
      });
      if (res.data?.user?.timezone) {
        setStoreTimezone(res.data.user.timezone);
      }
      if (res.data?.user?.roles?.includes('store_customer') && res.data?.user?.store_name) {
        setStoreName(res.data.user.store_name);
      } else {
        removeStoreName();
      }
      // 登录后刷新用户信息，确保 audit_status 等字段是最新的
      await fetchMe();
    } else {
      throw new Error(res?.message || t('登录失败'));
    }
  };

  const logout = () => {
    removeToken();
    removeStoreName();
    setUser(null);
    window.location.href = '/login';
  };

  const roles = user?.roles || [];
  const isStoreCustomer = roles.includes('store_customer');
  const isFinance = roles.includes('finance');
  const isAdmin = roles.includes('admin') || roles.includes('ops');
  const hasRole = useCallback((targetRoles: string[]) => targetRoles.some((r) => roles.includes(r)), [roles]);
  const auditStatus = user?.audit_status ?? null;
  const auditStatusNum = auditStatus !== null ? Number(auditStatus) : null;
  const canOrder = !isStoreCustomer || auditStatusNum === 1;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, fetchMe, isStoreCustomer, isFinance, isAdmin, hasRole, auditStatus, canOrder }}>
      {children}
    </AuthContext.Provider>
  );
};
